<template>
  <div>
    <component :is="getComponent(options.type)" v-if="show"/>
    <div v-for="option in multiOption" :key="option.type">
      <component :is="getComponent(option.type)" :option-data="option"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { defineAsyncComponent, ref } from "#imports";
import { useDialogStore } from "~/store/dialog/dialogStore";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import { type DialogStoreStateOption } from "~~/application/store/dialog/state";
import { markRaw } from "vue";

const dialogStore = useDialogStore();
const show = ref(false);
const options = ref({} as DialogStoreStateOption);
const multiOption = ref<Array<DialogStoreStateOption>>([])
const route = useRoute();

dialogStore.$subscribe((mutation, state) => {
    show.value = state.show;
    options.value = state.options;
    multiOption.value = state.multiOption;
});

// 매 렌더마다 새로 만들지 않고 동일 참조 반환
// Report 차트 응시자 관련 팝업 리 렌더 방지
const CandidateSlidePopupComp = markRaw(
    defineAsyncComponent(() =>
        import('~/components/Common/Dialog/dialog/Candidates/CandidateSlidePopup.vue')
    )
);

const getComponent = (type: DialogType | undefined) => {
  if (!type) {
    return '';
  }
  switch (type as DialogType) {
    case DialogType.CautionTabChangePopup:

      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Caution/CautionTabChangePopup.vue'));
    case DialogType.CautionMultiMonitorPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Caution/CautionMultiMonitorPopup.vue'));
    case DialogType.CautionShortCutPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Caution/CautionShortCutPopup.vue'));
    case DialogType.CookieAccept:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Cookie/CookieAccept.vue'));
    case DialogType.ContestAuthentication:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Contest/AuthenticationCodePopup.vue'));
    case DialogType.CheatingMultiMonitorCheck:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Cheating/MultiMonitorCheckPopup.vue'));
    case DialogType.CheatingScreenRecordingAgree:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Cheating/ScreenRecordingAgreePopup.vue'));
    case DialogType.TrackGuideViewPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/TrackGuideViewPopup.vue'));
    case DialogType.CandidateTimeLinePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Candidates/CandidateTimeLinePopup.vue'));
    case DialogType.CandidateSlidePopup:
      return CandidateSlidePopupComp;
    case DialogType.ProSkillAssureSamplePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ProSkillAssureSamplePopup.vue'));
    case DialogType.TrackDescriptionPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/TrackDescriptionPopup.vue'));
    case DialogType.LearningConfirmPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/LearningConfirmPopup/LearningConfirmPopup.vue'));
    case DialogType.LearningDirectoryRollback:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/LearningDirectoryRollbackPopup/LearningDirectoryRollbackPopup.vue'));
    case DialogType.LearningSampleAssessmentInfoPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/LearningConfirmPopup/LearningSampleAssessmentInfoPopup.vue'));
    case DialogType.TrackLevelGuidePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/TrackLevelGuidePopup.vue'));
    case DialogType.SendInvitationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/SendInvitationPopup.vue'));
    case DialogType.ProSkillAssureCandidatesDelete:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ProSkillAssureCandidatesDeletePopup.vue'));
    case DialogType.ProSkillAssureAssessmentDelete:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ProSkillAssureAssessmentDeletePopup.vue'));
    case DialogType.ResendEmailPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ResendEmailPopup.vue'));
    case DialogType.ProSkillAssureSlidePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ProSkillAssureSlidePopup.vue'));
    case DialogType.ProfileAboutMeInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/AboutMeInformationPopup.vue'));
    case DialogType.ProfileWorkExperienceInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/WorkExperienceInformationPopup.vue'));
    case DialogType.ProfileEducationInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/EducationInformationPopup.vue'));
    case DialogType.ProfileBasicInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/BasicInformationPopup.vue'));
    case DialogType.ProfilePersonalInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/PersonalInformationPopup.vue'));
    case DialogType.ProfileSkillsInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/SkillsInformationPopup.vue'));
    case DialogType.ProfileTrackLevelInformationPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Profile/TrackLevelInformationPopup.vue'));
    case DialogType.RecordingFullScreenPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Recording/RecordingFullScreenPopup.vue'));
    case DialogType.AgreeDeleteAccount:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/AgreeDeleteAccountPopup.vue'));
    case DialogType.PasswordChangePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/PasswordChangePopup.vue'));
    case DialogType.OrganizationLeaveConfirmPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationLeaveConfirmPopup.vue'));
    case DialogType.OrganizationCreatePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationCreatePopup.vue'));
    case DialogType.OrganizationMemberInvited:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/member/OrganizationMemberInvitedPopup.vue'));
    case DialogType.OrganizationMemberModify:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/member/OrganizationMemberModifyPopup.vue'));
    case DialogType.OrganizationMemberDelete:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationMemberDeletePopup.vue'));
    case DialogType.OrganizationMemberTransferAuthority:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationMemberTransferAuthorityPopup.vue'));
    case DialogType.OrganizationSubscribeCancelPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationSubscribeCancelPopup.vue'));
    case DialogType.OrganizationSubscribeRestartPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationSubscribeRestartPopup.vue'));
    case DialogType.GuidelinePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/GuideLine/GuidelinePopup.vue'));
    case DialogType.ProSkillAssureDurationGuidePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/ProSkillAssureDurationGuidePopup.vue'));
    case DialogType.ProSkillAssureTemplateSave:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/template/ProSkillAssureTemplateSave.vue'));
    case DialogType.ProSkillAssureTemplateManage:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/ProSkillAssure/template/ProSkillAssureTemplateManage.vue'));
    case DialogType.DesktopAppInstallPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/DesktopAppInstallPopup.vue'));
    case DialogType.ProctiveRecordingFailPopup:

      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Proctive/ProctiveRecordingFailPopup.vue'));
    case DialogType.ProctiveScreenPermissionGuidePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Proctive/ProctiveScreenPermissionGuidePopup.vue'));
    case DialogType.AssessmentScreenShareHelpPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Assessment/AssessmentScreenShareHelpPopup.vue'));
    case DialogType.ProctiveEnvironmentHelpPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Proctive/ProctiveEnvironmentHelpPopup.vue'));
    case DialogType.ReportEnterEmailPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Report/ReportEnterEmailPopup.vue'))
    case DialogType.ReportBulkEnterEmailPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Report/ReportBulkEnterEmailPopup.vue'))
    case DialogType.GenericAlert:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Generic/GenericAlertPopup.vue'));
    case DialogType.GenericConfirm:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/Generic/GenericConfirmPopup.vue'));
    case DialogType.OrganizationAddCreditsPopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationAddCreditsPopup.vue'));
    case DialogType.OrganizationCardChangePopup:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/settings/organization/OrganizationCardChangePopup.vue'));
    case DialogType.SessionExpired:
      return defineAsyncComponent(() => import('~/components/Common/Dialog/dialog/SessionExpired/SessionExpiredPopup.vue'));
    default:
      return '';
  }
}
</script>

<style>
@import "~/assets/scss/popup_style.scss";
</style>

